import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import {
  listCompletedOffboardings,
  OFFBOARDING_REASON_LABEL,
  type OffboardingReason,
  type CompletedOffboardingRow,
} from "@/lib/offboarding";

export const Route = createFileRoute("/_app/_hr/hr/exit-reviews")({
  component: ExitReviewsPage,
  head: () => ({ meta: [{ title: "Exit Interviews — HR" }] }),
});

function safeDate(v: string | null, fmt = "dd MMM yyyy"): string {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : format(d, fmt);
}

function ExitReviewsPage() {
  const { role, profile } = useAuth();
  const allowed = role === "admin" || profile?.department === "hr";

  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["offboarding-completed"],
    queryFn: listCompletedOffboardings,
    enabled: allowed,
  });

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return rows.filter((r) => {
      if (reasonFilter !== "all" && r.reason !== reasonFilter) return false;
      if (!s) return true;
      const name = r.employee?.full_name?.toLowerCase() ?? "";
      const eid = r.employee?.employee_id?.toLowerCase() ?? "";
      return name.includes(s) || eid.includes(s);
    });
  }, [rows, search, reasonFilter]);

  const summary = useMemo(() => {
    const total = rows.length;
    let yes = 0, no = 0, maybe = 0, unanswered = 0;
    for (const r of rows) {
      const j = r.exit_interview?.join_again;
      if (j === "yes") yes++;
      else if (j === "no") no++;
      else if (j === "maybe") maybe++;
      else unanswered++;
    }
    return { total, yes, no, maybe, unanswered };
  }, [rows]);

  if (!allowed) {
    return <div className="p-4 text-sm text-muted-foreground">You do not have access to exit interviews.</div>;
  }

  const headStyle = "uppercase text-[11px] font-semibold text-muted-foreground";

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Exit Interviews</h1>
          <p className="text-xs text-muted-foreground">
            Read-only review of completed offboardings — reason, last working day, and interview answers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or employee ID…"
            className="h-9 w-64 text-xs"
          />
          <Select value={reasonFilter} onValueChange={setReasonFilter}>
            <SelectTrigger className="h-9 w-40 text-xs"><SelectValue placeholder="Reason" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reasons</SelectItem>
              {(Object.keys(OFFBOARDING_REASON_LABEL) as OffboardingReason[]).map((r) => (
                <SelectItem key={r} value={r}>{OFFBOARDING_REASON_LABEL[r]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total exits" value={summary.total} />
        <StatCard label="Would rejoin" value={summary.yes} tone="pos" />
        <StatCard label="Would not" value={summary.no} tone="neg" />
        <StatCard label="Maybe" value={summary.maybe} />
        <StatCard label="No answer" value={summary.unanswered} muted />
      </div>

      <div className="rounded-lg border bg-card overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead className={headStyle} style={{ width: 100 }}>Employee ID</TableHead>
              <TableHead className={headStyle} style={{ width: 180 }}>Name</TableHead>
              <TableHead className={headStyle} style={{ width: 130 }}>Department</TableHead>
              <TableHead className={headStyle} style={{ width: 130 }}>Reason</TableHead>
              <TableHead className={headStyle} style={{ width: 110 }}>LWD</TableHead>
              <TableHead className={headStyle} style={{ width: 110 }}>Completed</TableHead>
              <TableHead className={headStyle} style={{ width: 110 }}>Would rejoin</TableHead>
              <TableHead className={`${headStyle} text-right`} style={{ width: 90 }}>Answers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={8} className="text-center text-xs text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-xs text-muted-foreground">No completed offboardings.</TableCell></TableRow>
            )}
            {filtered.map((r, idx) => {
              const rowBg = idx % 2 === 1 ? "#FAFAFA" : "transparent";
              const dept = DEPARTMENT_LABELS[(r.employee?.department ?? "") as Department] ?? (r.employee?.department ?? "—");
              const isOpen = expanded === r.id;
              return (
                <Fragment key={r.id}>
                  <TableRow style={{ minHeight: 52, backgroundColor: rowBg }}>
                    <TableCell className="font-mono text-xs">{r.employee?.employee_id ?? "—"}</TableCell>
                    <TableCell className="text-xs font-bold">{r.employee?.full_name ?? "(deleted)"}</TableCell>
                    <TableCell className="text-xs">{dept}</TableCell>
                    <TableCell className="text-xs">{OFFBOARDING_REASON_LABEL[r.reason]}</TableCell>
                    <TableCell className="text-xs">{safeDate(r.last_working_day)}</TableCell>
                    <TableCell className="text-xs">{safeDate(r.completed_at)}</TableCell>
                    <TableCell className="text-xs">
                      <JoinAgainPill v={r.exit_interview?.join_again ?? null} />
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        className="text-xs text-primary hover:underline"
                      >
                        {isOpen ? "Hide" : "View"}
                      </button>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow style={{ backgroundColor: "#F5F7FA" }}>
                      <TableCell colSpan={8} className="p-4">
                        <ExitInterviewDetail row={r} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, muted }: { label: string; value: number; tone?: "pos" | "neg"; muted?: boolean }) {
  const color =
    tone === "pos" ? "#0F766E" : tone === "neg" ? "#B91C1C" : muted ? "#6B7280" : "#111827";
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}

function JoinAgainPill({ v }: { v: "yes" | "no" | "maybe" | null | undefined }) {
  if (!v) return <span className="text-muted-foreground">—</span>;
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    yes: { bg: "#DCFCE7", fg: "#166534", label: "Yes" },
    no: { bg: "#FEE2E2", fg: "#991B1B", label: "No" },
    maybe: { bg: "#FEF3C7", fg: "#92400E", label: "Maybe" },
  };
  const s = map[v];
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ backgroundColor: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function ExitInterviewDetail({ row }: { row: CompletedOffboardingRow }) {
  const ei = row.exit_interview ?? {};
  const items: Array<[string, string | null | undefined]> = [
    ["Reason for leaving", ei.reason_for_leaving],
    ["Work environment", ei.work_environment],
    ["Suggested improvements", ei.improvements],
    ["Additional notes", ei.notes],
    ["Offboarding notes (HR)", row.notes],
  ];
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {items.map(([label, val]) => (
        <div key={label} className="rounded-md border bg-card p-3">
          <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
          <div className="mt-1 whitespace-pre-wrap text-xs">
            {val && val.trim() ? val : <span className="text-muted-foreground">—</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
